/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { asyncScheduler, fromEvent, SchedulerLike, Subscription, timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { linear } from '../scrolling/easing-function';
import { ScrollPlayer } from '../scrolling/scroll-player';
import { EasingFunction } from '../scrolling/scrolling.types';
import { getKeyFromDirection, getReverseDir } from '../services/spatial-navigation/spatial-navigation';
import { SpatialNavigationDirection } from '../services/spatial-navigation/spatial-navigation-polyfill';

const TakeoffDurationMultiplier = 1.25;
const LandingDurationMultiplier = 3;
const AnimationFallbackDurationMultiplier = 2;
const NavigationKeys = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

export type NavigationMotionPhase = 'takeoff' | 'cruise' | 'landing';

export interface NavigationMotionProfile {
    readonly phase: NavigationMotionPhase;
    readonly durationMs: number;
    readonly easingFunction: EasingFunction;
}

export type NavigationPlayerFactory = (profile: NavigationMotionProfile) => ScrollPlayer;

export interface ContinuousNavigationStep {
    readonly direction: SpatialNavigationDirection;
    readonly cadenceMs: number;
    readonly createPlayer?: NavigationPlayerFactory;
    readonly canContinue?: () => boolean;
    readonly ownsFocus?: () => boolean;
    readonly settle?: () => void;
}

interface ActiveStep {
    readonly request: ContinuousNavigationStep;
    readonly subscriptions: Subscription;
    progress: number;
}

export class ContinuousNavigationSession {
    private phase: NavigationMotionPhase | 'idle' = 'idle';
    private direction?: SpatialNavigationDirection;
    private activeStep?: ActiveStep;
    private request?: ContinuousNavigationStep;
    private inputAdmissionSubscription?: Subscription;
    private lifecycleSubscription?: Subscription;
    private repeatAllowedAt = Number.NEGATIVE_INFINITY;
    private acceptedRepeat = false;
    private destroyed = false;

    constructor(
        private readonly keyEventTarget: EventTarget = window,
        private readonly scheduler: SchedulerLike = asyncScheduler,
        private readonly documentEventTarget: EventTarget = document,
        private readonly isDocumentHidden: () => boolean = () => document.visibilityState === 'hidden'
    ) {}

    start(request: ContinuousNavigationStep): void {
        if (this.destroyed) {
            return;
        }
        this.validateCadence(request.cadenceMs);

        const continuesSequence = this.acceptedRepeat && request.direction === this.direction;
        this.acceptedRepeat = false;

        if (continuesSequence) {
            this.stopActiveStep();
        } else {
            this.cancel();
            this.direction = request.direction;
        }

        this.request = request;
        this.phase = continuesSequence ? 'cruise' : 'takeoff';
        if (!continuesSequence) {
            this.installInputAdmission();
            this.installLifecycleMonitoring();
        }
        if (request.createPlayer) {
            this.startAnimatedStep(request, this.phase);
        }
    }

    cancel(): void {
        const activeStep = this.activeStep;
        const inputAdmissionSubscription = this.inputAdmissionSubscription;
        const lifecycleSubscription = this.lifecycleSubscription;

        this.activeStep = undefined;
        this.inputAdmissionSubscription = undefined;
        this.lifecycleSubscription = undefined;
        this.request = undefined;
        this.direction = undefined;
        this.phase = 'idle';
        this.acceptedRepeat = false;
        this.repeatAllowedAt = Number.NEGATIVE_INFINITY;

        activeStep?.subscriptions.unsubscribe();
        inputAdmissionSubscription?.unsubscribe();
        lifecycleSubscription?.unsubscribe();
    }

    destroy(): void {
        if (!this.destroyed) {
            this.destroyed = true;
            this.cancel();
        }
    }

    private installInputAdmission(): void {
        const navigationKey = getKeyFromDirection(this.direction!);
        const inputAdmissionSubscription = new Subscription();
        this.inputAdmissionSubscription = inputAdmissionSubscription;

        inputAdmissionSubscription.add(
            fromEvent<KeyboardEvent>(this.keyEventTarget, 'keydown', { capture: true }).subscribe(event => {
                if (event.key !== navigationKey) {
                    return;
                }

                if (!this.canContinue()) {
                    if (this.request?.ownsFocus?.() === false) {
                        this.settleAndCancel();
                    } else {
                        this.cancel();
                    }
                    return;
                }

                const now = this.scheduler.now();
                if (now < this.repeatAllowedAt) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }

                const cadenceMs = this.request!.cadenceMs;
                const deadlineLag = now - this.repeatAllowedAt;
                this.repeatAllowedAt =
                    Number.isFinite(this.repeatAllowedAt) && deadlineLag < cadenceMs
                        ? this.repeatAllowedAt + cadenceMs
                        : now + cadenceMs;
                this.acceptedRepeat = true;
            })
        );

        inputAdmissionSubscription.add(
            fromEvent<KeyboardEvent>(this.keyEventTarget, 'keyup', { capture: true })
                .pipe(
                    filter(event => event.key === navigationKey),
                    take(1)
                )
                .subscribe(() => this.release(inputAdmissionSubscription))
        );
    }

    private installLifecycleMonitoring(): void {
        const navigationKey = getKeyFromDirection(this.direction!);
        const reverseNavigationKey = getKeyFromDirection(getReverseDir(this.direction!));
        const lifecycleSubscription = new Subscription();
        this.lifecycleSubscription = lifecycleSubscription;

        const cancelIfCurrent = () => {
            if (this.lifecycleSubscription === lifecycleSubscription) {
                this.cancel();
            }
        };
        const settleIfCurrent = () => {
            if (this.lifecycleSubscription === lifecycleSubscription) {
                this.settleAndCancel();
            }
        };
        lifecycleSubscription.add(
            fromEvent<KeyboardEvent>(this.keyEventTarget, 'keydown', { capture: true }).subscribe(event => {
                if (event.key === reverseNavigationKey) {
                    cancelIfCurrent();
                } else if (event.key !== navigationKey && NavigationKeys.has(event.key)) {
                    settleIfCurrent();
                }
            })
        );
        lifecycleSubscription.add(fromEvent(this.keyEventTarget, 'blur').pipe(take(1)).subscribe(settleIfCurrent));
        lifecycleSubscription.add(fromEvent(this.keyEventTarget, 'pagehide').pipe(take(1)).subscribe(cancelIfCurrent));
        lifecycleSubscription.add(
            fromEvent(this.documentEventTarget, 'focusin').subscribe(() => {
                if (this.request?.ownsFocus?.() === false) {
                    settleIfCurrent();
                }
            })
        );
        lifecycleSubscription.add(
            fromEvent(this.documentEventTarget, 'visibilitychange').subscribe(() => {
                if (this.isDocumentHidden()) {
                    settleIfCurrent();
                }
            })
        );
    }

    private startAnimatedStep(
        request: ContinuousNavigationStep,
        phase: NavigationMotionPhase,
        landingDurationMs?: number
    ): void {
        const profile = this.getMotionProfile(request.cadenceMs, phase, landingDurationMs);
        let player: ScrollPlayer;
        try {
            player = request.createPlayer!(profile);
        } catch (error) {
            this.cancel();
            throw error;
        }

        const step = this.createActiveStep(request);
        step.subscriptions.add(
            player
                .onScrollEnd()
                .pipe(take(1))
                .subscribe(() => this.completeStep(step, false))
        );
        step.subscriptions.add(
            timer(profile.durationMs * AnimationFallbackDurationMultiplier, this.scheduler)
                .pipe(take(1))
                .subscribe(() => this.completeStep(step, true))
        );
        step.subscriptions.add(
            player.start().subscribe({
                next: progress => {
                    if (this.isCurrent(step)) {
                        step.progress = progress;
                    }
                },
                error: () => this.completeStep(step, true)
            })
        );
    }

    private createActiveStep(request: ContinuousNavigationStep): ActiveStep {
        this.stopActiveStep();
        const step: ActiveStep = {
            request,
            subscriptions: new Subscription(),
            progress: 0
        };
        this.activeStep = step;
        return step;
    }

    private stopActiveStep(): void {
        const activeStep = this.activeStep;
        if (activeStep) {
            this.activeStep = undefined;
            activeStep.subscriptions.unsubscribe();
        }
    }

    private completeStep(step: ActiveStep, settle: boolean): void {
        if (!this.isCurrent(step)) {
            return;
        }

        this.activeStep = undefined;
        if (settle) {
            try {
                step.request.settle?.();
            } catch (error) {
                step.subscriptions.unsubscribe();
                this.cancel();
                throw error;
            }
        }
        step.subscriptions.unsubscribe();

        if (this.phase === 'landing') {
            this.cancel();
        } else {
            this.phase = 'cruise';
        }
    }

    private release(inputAdmissionSubscription: Subscription): void {
        if (this.inputAdmissionSubscription !== inputAdmissionSubscription) {
            return;
        }

        this.inputAdmissionSubscription = undefined;
        inputAdmissionSubscription.unsubscribe();
        this.acceptedRepeat = false;

        const activeStep = this.activeStep;
        if (!activeStep?.request.createPlayer || this.phase === 'landing') {
            this.cancel();
            return;
        }

        const remainingProgress = 1 - activeStep.progress;
        if (remainingProgress <= 0) {
            this.cancel();
            return;
        }

        this.phase = 'landing';
        this.startAnimatedStep(
            activeStep.request,
            'landing',
            Math.max(1, remainingProgress * activeStep.request.cadenceMs * LandingDurationMultiplier)
        );
    }

    private settleAndCancel(): void {
        const settle = this.activeStep?.request.settle;
        this.cancel();
        settle?.();
    }

    private getMotionProfile(
        cadenceMs: number,
        phase: NavigationMotionPhase,
        landingDurationMs?: number
    ): NavigationMotionProfile {
        switch (phase) {
            case 'takeoff':
                return {
                    phase,
                    durationMs: cadenceMs * TakeoffDurationMultiplier,
                    easingFunction: progress => Math.pow(progress, TakeoffDurationMultiplier)
                };
            case 'cruise':
                return { phase, durationMs: cadenceMs, easingFunction: linear };
            case 'landing':
                return {
                    phase,
                    durationMs: landingDurationMs!,
                    easingFunction: progress => 1 - Math.pow(1 - progress, LandingDurationMultiplier)
                };
        }
    }

    private isCurrent(step: ActiveStep): boolean {
        return !this.destroyed && this.activeStep === step;
    }

    private canContinue(): boolean {
        return !!this.request && this.request.canContinue?.() !== false && this.request.ownsFocus?.() !== false;
    }

    private validateCadence(cadenceMs: number): void {
        if (!Number.isFinite(cadenceMs) || cadenceMs <= 0) {
            throw new Error('cadenceMs must be a finite duration greater than zero.');
        }
    }
}
